import { useState, useEffect } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowDownToLine, Copy, ShieldCheck, Clock, Zap } from "lucide-react";
import api from "@/lib/api";
import { money, dateFmt, shortAddr } from "@/lib/format";

export default function Deposit() {
  const [addr, setAddr] = useState(null);
  const [rows, setRows] = useState([]);

  const load = async () => {
    const [a, d] = await Promise.all([api.get("/me/deposit-address"), api.get("/me/deposits")]);
    setAddr(a.data);
    setRows(d.data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Load failed"));
    const iv = setInterval(() => api.get("/me/deposits").then(({ data }) => setRows(data)).catch(() => {}), 20000);
    return () => clearInterval(iv);
  }, []);

  const copyWallet = () => { navigator.clipboard.writeText(addr.address); toast.success("Deposit address copied"); };

  return (
    <div className="space-y-6" data-testid="deposit-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2"><ArrowDownToLine className="w-6 h-6 text-amber-400" /> Deposit USDT</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><ShieldCheck className="w-5 h-5 text-emerald-400" /><span className="font-semibold">Your Unique USDT · BEP20 Deposit Address</span></div>
          {addr?.address ? (
            <>
              <div className="flex flex-col items-center">
                <div className="bg-white p-3 rounded-2xl" data-testid="deposit-qr">
                  <QRCodeCanvas value={addr.address} size={168} bgColor="#ffffff" fgColor="#050507" level="M" includeMargin={false} />
                </div>
                <div className="text-xs text-slate-500 mt-3">Scan with your wallet app to deposit</div>
              </div>
              <div className="text-xs text-slate-400 uppercase tracking-wide mt-6 mb-2">Or copy address (send only USDT BEP20 / BSC)</div>
              <div className="flex gap-2">
                <input readOnly value={addr.address} data-testid="deposit-address-input" className="flex-1 bg-black/40 border border-amber-500/20 rounded-xl px-4 py-3 font-mono-fin text-xs text-amber-300 outline-none" />
                <button data-testid="copy-address-btn" onClick={copyWallet} className="gold-btn px-4 rounded-xl"><Copy className="w-4 h-4" /></button>
              </div>
              <div className="mt-4 rounded-xl border p-3 text-xs flex items-center gap-2 bg-emerald-500/10 border-emerald-500/20 text-emerald-300">
                <Zap className="w-4 h-4 shrink-0" />
                Deposits are detected on-chain and credited to your balance automatically — no manual confirmation needed. It usually reflects within a minute of network confirmation.
              </div>
            </>
          ) : <div className="text-slate-500 text-sm">Generating your deposit address...</div>}
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" /> Deposit History</h3>
          {rows.length === 0 ? <p className="text-slate-500 text-sm">No deposits yet. Send USDT to your address above and it will appear here automatically.</p> : (
            <div className="space-y-3">
              {rows.map((r) => (
                <div key={r.id} className="rounded-xl bg-black/30 border border-amber-500/15 p-4" data-testid={`deposit-row-${r.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-mono-fin font-bold gold-text">{money(r.amount)}</div>
                      <div className="text-xs text-slate-500 font-mono-fin mt-1">{shortAddr(r.txid)}{r.auto && <span className="ml-2 text-emerald-400">auto</span>}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${r.status === "verified" ? "bg-emerald-500/15 text-emerald-400" : r.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{r.status === "verified" ? "credited" : r.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">{dateFmt(r.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
